import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../lib/adminAuth";
import BrandMark from "../components/BrandMark";

export default function AdminLogin() {
  const { user, loading, login } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "admin@bharatjeswani.com", password: "" });
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/admin/inquiries" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.password.trim()) {
      toast.error("Password required");
      return;
    }
    setSubmitting(true);
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      toast.success("Welcome back");
      navigate("/admin/inquiries", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[var(--bjc-bg)]" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-10">
          <BrandMark height={120} />
        </div>

        <div className="bjc-card p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck size={16} className="text-[var(--bjc-gold)]" />
            <span className="bjc-overline">Restricted Access</span>
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--bjc-text)] leading-tight mt-3">
            Sign in to continue.
          </h1>
          <p className="mt-3 text-sm text-[var(--bjc-text-muted)]">
            For viewing and managing client inquiries received via the website.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-8" data-testid="admin-login-form" noValidate>
            <label className="block">
              <span className="block bjc-overline mb-3">Admin Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="admin-input-email"
                className="bjc-input"
                autoComplete="username"
              />
            </label>

            <label className="block">
              <span className="block bjc-overline mb-3">Password</span>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="admin-input-password"
                className="bjc-input"
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="bjc-btn-primary w-full justify-center disabled:opacity-60"
            >
              {submitting ? (<><Loader2 size={14} className="animate-spin" /> Signing in</>) : (<>Sign In <ArrowRight size={14} /></>)}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-[10px] tracking-[0.2em] uppercase text-[var(--bjc-text-faint)]">
          Authorised personnel only
        </p>
      </div>
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bjc-bg)]">
      <Loader2 size={20} className="animate-spin text-[var(--bjc-gold)]" />
    </div>
  );
}
